import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../../../../components/button/Button";
import DisplayContainer from "../../../../../components/displayContainer/DisplayContainer";
import RefreshIcon from "../../../../../components/icons/RefreshIcon";
import Loader from "../../../../../components/loader/Loader";
import OneTournamentDetailsTemplate from "../../../../../components/oneTournamentDetailsTemplate/OneTournamentDetailsTemplate";
import TextWrapper from "../../../../../components/textWrapper/TextWrapper";
import { fetchTournamentDetailsTemplates } from "../../../../../features/tournamentDetailsTemplates/tournamentDetailsTemplatesSlice";
import styles from "./allTournamentDetailsTemplates.module.css";

const AllTournamentDetailsTemplates = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { list, fetchStatus, currentPage, totalPages } = useSelector(
    (state) => state.tournamentDetailsTemplates,
  );

  const listRef = useRef();

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    listRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
    try {
      await dispatch(fetchTournamentDetailsTemplates({ page: 1 })).unwrap();
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatch]);

  const handleScroll = useCallback(() => {
    const container = listRef.current;
    if (!container || fetchStatus === "loading") return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      currentPage < totalPages
    ) {
      dispatch(fetchTournamentDetailsTemplates({ page: currentPage + 1 }));
    }
  }, [dispatch, fetchStatus, currentPage, totalPages]);

  useEffect(() => {
    if (fetchStatus === "idle") {
      dispatch(fetchTournamentDetailsTemplates({ page: 1 }));
    }
  }, [dispatch, fetchStatus]);

  useEffect(() => {
    const container = listRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const hasTemplates = Array.isArray(list) && list.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.headerTitleGroup}>
          <TextWrapper textStyle="textTitle">قوالب تفاصيل البطولة</TextWrapper>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => {
              Promise.resolve(handleRefresh()).catch(() => {});
            }}
            disabled={fetchStatus === "loading" || isRefreshing}
            title="تحديث القوالب"
            aria-label="تحديث القوالب"
          >
            {isRefreshing ? (
              <Loader size={18} color="var(--color-accent-primary)" />
            ) : (
              <RefreshIcon className={styles.refreshIcon} />
            )}
          </button>
        </div>
        <Button
          size="midSize"
          variant="surfaceVariant"
          functionallity={() =>
            navigate("/dashboard/tournaments/add-tournament-details-template")
          }
        >
          <TextWrapper textStyle="textMid">اضافة قالب جديد</TextWrapper>
        </Button>
      </div>

      <div className={styles.listWrapper}>
        <DisplayContainer ref={listRef} className={styles.displayContainer}>
          {hasTemplates
            ? list.map((template) => (
                <OneTournamentDetailsTemplate
                  key={template._id}
                  template={template}
                />
              ))
            : fetchStatus !== "loading" && (
                <div className={styles.emptyState}>
                  <TextWrapper textStyle="textMid">
                    لا توجد قوالب بعد
                  </TextWrapper>
                </div>
              )}
          {fetchStatus === "loading" && (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          )}
        </DisplayContainer>
      </div>
    </div>
  );
};

export default AllTournamentDetailsTemplates;
